import {
  HamburgerMenu,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  useDynamicCallback,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  type Column,
  type ColumnDef,
  type FilterableProperty,
  type MarketExecutionReport,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise';
import { compact, isNil, last } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { colIDToFilterBuilderKey } from './useMarketExecutionReportFilter';

export function useMarketExecutionReportMenu({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}): {
  columns: Column[];
  getContextMenuItems: (params: GetContextMenuItemsParams) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();

  const dialogs = useMemo(() => <>{jsonModal}</>, [jsonModal]);

  const columns = useMemo(
    () =>
      compact([
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => <HamburgerMenu {...params} onShowJson={handleClickJson} />,
          },
        },
      ] satisfies ColumnDef<MarketExecutionReport>[]),
    [handleClickJson]
  );

  const defaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams) => {
    const data = params?.node?.data;
    const groupData = params?.node?.groupData;
    if (isNil(data) && isNil(groupData)) {
      return [];
    }

    const items = compact([
      ...filterByCellValueMenuItem({ params, filterableProperties, openClause, colIDToFilterBuilderKey, mixpanel }),
      ...(data ? [getShowJSONContextItem({ params, handleClickJson }), 'separator'] : []),
      ...defaultContextMenuItems(params),
    ]);
    if (last(items) === 'separator') {
      items.splice(items.length - 1, 1);
    }
    return items;
  });

  return { columns, getContextMenuItems, dialogs };
}
